import React, { useEffect, useState } from 'react';
import { ParticipantView } from '@views';
import { Box, Fab, Typography, Button } from '@mui/material';
import { palette } from '@app/palette';
import { AdminJailCardViewCreator } from '@viewCreators/AdminJailCardViewCreator';
import logger from '@utils/logger';
import { JailCardStore } from '@stores/JailCardStore';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';

interface CohortUserJailCardEditorProps {
  cohortUser: ParticipantView;
}

type AdminJailCardView = {
  id: number;
  used: boolean;
  usedOn: string | null;
};

export const CohortUserJailCardEditor: React.FC<CohortUserJailCardEditorProps> = ({ cohortUser }) => {
  const jailCardStore = new JailCardStore();
  const [rows, setRows] = useState<AdminJailCardView[]>([]);
  const [loading, setLoading] = useState(false);

  // fetch on mount or when user changes
  useEffect(() => {
    loadJailCards();
  }, [cohortUser]);

  const loadJailCards = async () => {
    setLoading(true);
    try {
      const view = await new AdminJailCardViewCreator().CreateView(cohortUser.id);
      setRows(view.jailCards);
    } catch (err) {
      logger.error('Failed to fetch jail cards', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddJailCard = async () => {
    if (!window.confirm('Add a new jail card?')) return;
    try {
      const resp = await jailCardStore.Write(cohortUser.id);
      if (resp.status === 'SUCCESS') {
        window.alert('Jail card created');
        loadJailCards();
      } else {
        window.alert('Failed to create jail card');
      }
    } catch (err) {
      logger.error(err);
      window.alert('Error creating jail card');
    }
  };

  const handleSaveRow = async (jailCard: AdminJailCardView) => {
    try {
      await jailCardStore.Update(jailCard.id, cohortUser.id, jailCard.used, jailCard.usedOn);
      window.alert('Saved!');
      loadJailCards();
    } catch (err) {
      logger.error('Save failed', err);
      window.alert('Save failed');
    }
  };

  // Delete a jail card
  const handleDeleteRow = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this jail card?')) return;
    try {
      await jailCardStore.Delete(id, cohortUser.id);
      window.alert('Jail card deleted');
      loadJailCards();
    } catch (err) {
      logger.error('Delete failed', err);
      window.alert('Delete failed');
    }
  };

  const columns: GridColDef<AdminJailCardView>[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'used',
      headerName: 'Used?',
      type: 'boolean',
      editable: true,
      width: 150,
    },
    {
      field: 'usedOn',
      headerName: 'Used On',
      editable: true,
      width: 180,
      renderEditCell: (params) => (
        <DatePicker
          value={params.value ? DateTime.fromISO(params.value) : null}
          onChange={(dt) =>
            params.api.setEditCellValue({
              id: params.id,
              field: 'usedOn',
              value: dt?.toISODate() ?? null,
            })
          }
          slotProps={{ textField: { size: 'small' } }}
        />
      ),
    },
    {
      field: '__actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<AdminJailCardView>) => (
        <>
          <Button size="small" variant="contained" onClick={() => handleSaveRow(params.row)} sx={{ mr: 1 }}>
            Save
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteRow(params.row.id)}>
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <Box
      borderRadius={8}
      border={1}
      borderColor={palette.background.border}
      display="block"
      position="relative"
      padding={4}
      margin={4}
    >
      <Typography
        variant="subtitle1"
        sx={{
          backgroundColor: palette.background.paper,
          position: 'absolute',
          top: -32,
          left: 48,
          padding: 2,
        }}
      >
        Jail Cards
      </Typography>

      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        editMode="cell"
        sx={{
          mb: 2,
          // Update header styles
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily: 'Roboto, serif',

            letterSpacing: '0.5px',
          },
          // Update cell styles
          '& .MuiDataGrid-cell': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.9rem',
          },
          // Update cell editing styles
          '& .MuiDataGrid-cell--editing': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.9rem',
          },
          // Update rows per page styles
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.875rem',
          },
          // Update page indicator “1–10 of 100” styles
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.875rem',
          },
          // Update pagination arrows / page buttons
          '& .MuiPaginationItem-root': {
            fontFamily: 'Roboto, serif',
            fontSize: '0.875rem',
          },
        }}
      />

      <Fab variant="extended" onClick={handleAddJailCard}>
        Add Jail Card
      </Fab>
    </Box>
  );
};
